import { useState, useEffect, useCallback } from "react";
import { t } from "@/lib/i18n";
import { useNavigate } from "react-router-dom";
import { Lightning, Trash, Sparkle, Plus, X, ListMagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageShell } from "@/components/PageShell";
import { MailboxSwitcher, useMailboxApi } from "@/components/MailboxSwitcher";
import api, { errDetail } from "@/lib/api";

const ACTION_LABEL = { delete: ["rules.act_delete", "Move to Trash"], archive: ["rules.act_archive", "Archive"], spam: ["rules.act_spam", "Mark as Spam"], star: ["rules.act_star", "Star"], auto_reply: ["rules.act_auto_reply", "Auto-draft reply"], whatsapp_notify: ["rules.act_wa", "WhatsApp notify"], move_to_folder: ["rules.act_move", "Move to folder"] };
const TRIGGER_LABEL = { from_contains: ["rules.trig_from", "Sender contains"], subject_contains: ["rules.trig_subject", "Subject contains"], body_contains: ["rules.trig_body", "Body contains"], category_is: ["rules.trig_category", "Category is"] };

const EMPTY_FORM = { name: "", trigger_type: "from_contains", trigger_value: "", ai_condition: "", action_type: "archive", action_value: "", apply_scope: "future" };

export default function RulesPage() {
  const { mailbox, setMailbox, mbApi } = useMailboxApi();
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [tab, setTab] = useState("rules");
  const [fups, setFups] = useState([]);
  const [folders, setFolders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiDraft, setAiDraft] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPast, setAiPast] = useState(true);
  const [aiFuture, setAiFuture] = useState(true);
  const bumpTokens = () => import("@/components/TokenWidget").then((m) => m.refreshTokenWidget()).catch(() => {});

  const loadFups = useCallback(() => mbApi.get("/followups").then((r) => setFups(r.data)).catch(() => {}), [mbApi]);
  const load = useCallback(() => mbApi.get("/rules").then((r) => setRules(r.data)), [mbApi]);
  useEffect(() => { load(); loadFups(); mbApi.get("/folders").then((r) => setFolders(r.data)).catch(() => {}); }, [load, loadFups]);

  const cancelFup = async (f) => {
    try { await mbApi.delete(`/followups/${f.id}`); toast.success(t("rules.fup_cancelled_toast", "Follow-up cancelled")); loadFups(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const FUP_STATUS = { pending: ["bg-amber-100 text-amber-700", t("rules.fup_waiting", "Waiting")], done: ["bg-green-100 text-green-700", t("rules.fup_fired", "Fired")],
    cancelled: ["bg-gray-100 text-gray-500", t("rules.fup_cancelled", "Cancelled")], cancelled_reply_arrived: ["bg-blue-100 text-blue-700", t("rules.fup_reply", "Reply arrived ✓")],
    error: ["bg-red-100 text-red-600", t("rules.fup_error", "Error")] };

  const toggle = async (r) => {
    try { await mbApi.patch(`/rules/${r.id}`, { active: !r.active }); load(); } catch (e) { toast.error(errDetail(e)); }
  };
  const remove = async (r) => {
    try { await mbApi.delete(`/rules/${r.id}`); toast.success(t("rules.deleted", "Rule deleted")); load(); } catch (e) { toast.error(errDetail(e)); }
  };

  const runPreview = async (src) => {
    setPreviewing(true);
    try {
      const body = src || { trigger_type: form.trigger_type, trigger_value: form.trigger_value, ai_condition: form.ai_condition };
      const { data } = await mbApi.post("/rules/preview", body);
      setPreview(data);
      if (data.ai_evaluated) bumpTokens();
    } catch (e) { toast.error(errDetail(e)); }
    setPreviewing(false);
  };

  const createManual = async () => {
    try {
      const { data } = await mbApi.post("/rules", form);
      toast.success(t("rules.created", "Rule created") + (data.applied_now ? ` — ${t("rules.applied_n", "applied to {n} past emails", { n: data.applied_now })}` : ""));
      setShowForm(false); setForm(EMPTY_FORM); setPreview(null); load(); bumpTokens();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const aiBuild = async () => {
    if (!aiText.trim()) return;
    setAiBusy(true); setAiDraft(null);
    try {
      const { data } = await mbApi.post("/rules/ai-build", { instruction: aiText });
      setAiDraft(data);
      bumpTokens();
    } catch (e) { toast.error(errDetail(e)); }
    setAiBusy(false);
  };

  const aiCreate = async () => {
    if (!aiPast && !aiFuture) { toast.error(t("rules.pick_scope", "Choose past, future or both")); return; }
    const scope = aiPast && aiFuture ? "both" : (aiPast ? "past" : "future");
    try {
      const r = aiDraft.rule;
      const { data } = await mbApi.post("/rules", { name: r.name, trigger_type: r.trigger_type, trigger_value: r.trigger_value,
        ai_condition: r.ai_condition, action_type: r.action_type, action_value: r.action_value, apply_scope: scope });
      toast.success(t("rules.created", "Rule created") + (data.applied_now ? ` — ${t("rules.applied_n", "applied to {n} past emails", { n: data.applied_now })}` : ""));
      setAiDraft(null); setAiText(""); load(); bumpTokens();
      mbApi.get("/folders").then((x) => setFolders(x.data)).catch(() => {});
    } catch (e) { toast.error(errDetail(e)); }
  };

  const folderName = (id) => folders.find((f) => f.id === id)?.name || id;

  const openLogs = async (r) => {
    setLogsLoading(true); setLogs({ rule: { id: r.id, name: r.name, hits: r.hits || 0 }, items: [] });
    try { const { data } = await mbApi.get(`/rules/${r.id}/logs`); setLogs(data); }
    catch (e) { toast.error(errDetail(e)); setLogs(null); }
    setLogsLoading(false);
  };

  const relTime = (iso) => {
    if (!iso) return "";
    const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 60) return t("rules.mins_ago", "{n}m ago", { n: Math.max(mins, 1) });
    if (mins < 1440) return t("rules.hours_ago", "{n}h ago", { n: Math.round(mins / 60) });
    return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const ACTION_PAST = { delete: t("rules.log_deleted", "Moved to Trash"), archive: t("rules.log_archived", "Archived"), spam: t("rules.log_spam", "Marked as Spam"), star: t("rules.log_starred", "Starred"), auto_reply: t("rules.log_replied", "Auto-drafted reply"), whatsapp_notify: t("rules.log_wa", "WhatsApp notified"), move_to_folder: t("rules.log_moved", "Moved to folder") };

  const ruleActionText = (r) => {
    const base = t(...(ACTION_LABEL[r.action_type] || [r.action_type, r.action_type]));
    return r.action_type === "move_to_folder" ? `${base} 📁 ${folderName(r.action_value)}` : base;
  };

  return (
    <PageShell title={t("rules.title", "Automation Rules")} subtitle={t("rules.subtitle2", "Create rules by hand, with the AI builder, or by talking to your Email Agent.")} testId="rules-page">
      <MailboxSwitcher mailbox={mailbox} onChange={setMailbox} />
      <div className="flex gap-2 mb-4">
        <button data-testid="rules-tab" onClick={() => setTab("rules")}
          className={`text-sm font-semibold rounded-md px-4 py-1.5 ${tab === "rules" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"}`}>{t("rules.tab_rules", "Rules")}</button>
        <button data-testid="followups-tab" onClick={() => { setTab("followups"); loadFups(); }}
          className={`text-sm font-semibold rounded-md px-4 py-1.5 ${tab === "followups" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"}`}>
          {t("rules.tab_followups", "Follow-ups")}{fups.filter((f) => f.status === "pending").length > 0 ? ` (${fups.filter((f) => f.status === "pending").length})` : ""}</button>
      </div>
      {tab === "followups" ? (
        <div className="space-y-2" data-testid="followups-list">
          {fups.map((f) => (
            <div key={f.id} data-testid={`followup-card-${f.id}`} className="border border-gray-200 rounded-md p-4 flex items-start gap-3">
              <div className="w-8 h-8 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">⏰</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900">{f.note}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {f.kind === "no_reply" ? t("rules.fup_noreply", "If no reply") : t("rules.fup_reminder", "Reminder")} · {t("rules.fup_due", "due")} {f.due_display || f.due_utc}{f.contact ? ` · ${t("rules.fup_waiting_on", "waiting on {who}", { who: f.contact })}` : ""}
                </div>
              </div>
              <span className={`text-xs font-semibold rounded-full px-3 py-1 shrink-0 ${(FUP_STATUS[f.status] || FUP_STATUS.error)[0]}`}>{(FUP_STATUS[f.status] || FUP_STATUS.error)[1]}</span>
              {f.status === "pending" && (
                <button data-testid={`followup-cancel-${f.id}`} onClick={() => cancelFup(f)}
                  className="text-gray-400 hover:text-red-600 transition-colors p-1 shrink-0"><Trash size={15} /></button>
              )}
            </div>
          ))}
          {fups.length === 0 && <p data-testid="no-followups-message" className="text-sm text-gray-400">{t("rules.fup_none", "No follow-ups yet — tell the agent e.g. \"Remind me Thursday to email Monica\" or \"Follow up if she doesn't reply by Friday\".")}</p>}
        </div>
      ) : (<>

      {/* AI Rule Builder */}
      <div data-testid="ai-rule-builder" className="border border-blue-200 bg-blue-50/50 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={16} className="text-blue-600" />
          <span className="text-sm font-bold text-gray-900">{t("rules.ai_title", "AI Rule Builder")}</span>
        </div>
        <div className="flex gap-2">
          <textarea data-testid="ai-rule-input" rows={2} value={aiText} onChange={(e) => setAiText(e.target.value)}
            placeholder={t("rules.ai_ph", "Describe the rule — e.g. \"Move all emails from billing@holistor.com where the payment was confirmed to folder Pagos\"")}
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-white" />
          <button data-testid="ai-rule-preview-btn" onClick={aiBuild} disabled={aiBusy || !aiText.trim()}
            className="self-start bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-2 transition-colors disabled:opacity-50">
            {aiBusy ? t("rules.ai_thinking", "Thinking…") : t("rules.ai_preview", "Preview ▶")}
          </button>
        </div>
        {aiDraft && (
          <div data-testid="ai-rule-draft" className="mt-3 bg-white border border-gray-200 rounded-md p-3">
            <div className="text-xs font-bold text-gray-500 mb-1.5">{t("rules.ai_understood", "AI UNDERSTOOD")}</div>
            <div className="text-sm text-gray-800">
              <span className="font-medium">{t(...(TRIGGER_LABEL[aiDraft.rule.trigger_type] || [aiDraft.rule.trigger_type, aiDraft.rule.trigger_type]))}</span> "{aiDraft.rule.trigger_value}"
              {aiDraft.rule.ai_condition && <span> · <span className="text-purple-700 font-medium">⚡ AI: "{aiDraft.rule.ai_condition}"</span></span>}
              {" → "}<span className="font-medium">{aiDraft.rule.action_type === "move_to_folder" ? `${t("rules.act_move", "Move to folder")} 📁 ${aiDraft.rule.folder_name}${aiDraft.rule.folder_created ? ` (${t("rules.folder_new", "new folder")})` : ""}` : t(...(ACTION_LABEL[aiDraft.rule.action_type] || [aiDraft.rule.action_type, aiDraft.rule.action_type]))}</span>
            </div>
            <div className="text-xs font-bold text-gray-500 mt-3 mb-1">{t("rules.ai_matches", "LIVE PREVIEW — {n} matching emails", { n: aiDraft.preview.count })}</div>
            <div data-testid="ai-rule-preview-list" className="max-h-32 overflow-y-auto scroll-thin space-y-0.5">
              {aiDraft.preview.items.map((it) => (
                <div key={it.id} className="text-xs text-gray-600 truncate">✉ {it.subject} <span className="text-gray-400">· {it.from_email}</span></div>
              ))}
              {aiDraft.preview.count === 0 && <div className="text-xs text-gray-400">{t("rules.no_matches", "No past emails match — the rule will still watch future emails.")}</div>}
            </div>
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-700">
              <label className="flex items-center gap-1.5 cursor-pointer"><input data-testid="ai-rule-past-cb" type="checkbox" checked={aiPast} onChange={(e) => setAiPast(e.target.checked)} /> {t("rules.apply_past", "Apply to past emails")}</label>
              <label className="flex items-center gap-1.5 cursor-pointer"><input data-testid="ai-rule-future-cb" type="checkbox" checked={aiFuture} onChange={(e) => setAiFuture(e.target.checked)} /> {t("rules.apply_future", "Apply to future emails")}</label>
              <button data-testid="ai-rule-create-btn" onClick={aiCreate}
                className="ml-auto bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-md px-4 py-1.5 transition-colors">✔ {t("rules.create", "Create rule")}</button>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-gray-500">{t("rules.count", "{n} rules", { n: rules.length })}</span>
        <button data-testid="new-rule-btn" onClick={() => { setShowForm(!showForm); setPreview(null); }}
          className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800">
          <Plus size={14} weight="bold" /> {showForm ? t("common.close", "Close") : t("rules.new", "New rule")}
        </button>
      </div>

      {showForm && (
        <div data-testid="manual-rule-form" className="border border-gray-200 rounded-lg p-4 mb-4 space-y-3">
          <input data-testid="rule-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={t("rules.name_ph", "Rule name (optional)")} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <div className="flex gap-2 flex-wrap">
            <select data-testid="rule-trigger-select" value={form.trigger_type} onChange={(e) => setForm({ ...form, trigger_type: e.target.value })}
              className="border border-gray-300 rounded-md px-2 py-2 text-sm">
              {Object.keys(TRIGGER_LABEL).map((k) => <option key={k} value={k}>{t(...TRIGGER_LABEL[k])}</option>)}
            </select>
            <input data-testid="rule-trigger-value-input" value={form.trigger_value} onChange={(e) => setForm({ ...form, trigger_value: e.target.value })}
              placeholder={t("rules.trig_val_ph", "text to match — e.g. billing@holistor.com")} className="flex-1 min-w-[180px] border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <input data-testid="rule-ai-condition-input" value={form.ai_condition} onChange={(e) => setForm({ ...form, ai_condition: e.target.value })}
            placeholder={t("rules.ai_cond_ph", "⚡ Optional AI condition — e.g. \"the payment was confirmed\" (uses tokens)")}
            className="w-full border border-purple-200 bg-purple-50/40 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
          <div className="flex gap-2 flex-wrap items-center">
            <span className="text-sm text-gray-500">→</span>
            <select data-testid="rule-action-select" value={form.action_type} onChange={(e) => setForm({ ...form, action_type: e.target.value, action_value: "" })}
              className="border border-gray-300 rounded-md px-2 py-2 text-sm">
              {Object.keys(ACTION_LABEL).map((k) => <option key={k} value={k}>{t(...ACTION_LABEL[k])}</option>)}
            </select>
            {form.action_type === "move_to_folder" && (
              <select data-testid="rule-folder-select" value={form.action_value} onChange={(e) => setForm({ ...form, action_value: e.target.value })}
                className="border border-gray-300 rounded-md px-2 py-2 text-sm">
                <option value="">{t("rules.pick_folder", "Pick a folder…")}</option>
                {folders.map((f) => <option key={f.id} value={f.id}>📁 {f.name}</option>)}
              </select>
            )}
            {form.action_type === "auto_reply" && (
              <input data-testid="rule-reply-input" value={form.action_value} onChange={(e) => setForm({ ...form, action_value: e.target.value })}
                placeholder={t("rules.reply_ph", "Auto-reply text")} className="flex-1 min-w-[180px] border border-gray-300 rounded-md px-3 py-2 text-sm" />
            )}
            <select data-testid="rule-scope-select" value={form.apply_scope} onChange={(e) => setForm({ ...form, apply_scope: e.target.value })}
              className="border border-gray-300 rounded-md px-2 py-2 text-sm ml-auto">
              <option value="future">{t("rules.scope_future", "Future emails only")}</option>
              <option value="past">{t("rules.scope_past", "Past emails only")}</option>
              <option value="both">{t("rules.scope_both", "Past + future")}</option>
            </select>
          </div>
          {preview && (
            <div data-testid="manual-rule-preview" className="bg-gray-50 border border-gray-200 rounded-md p-2.5">
              <div className="text-xs font-bold text-gray-500 mb-1">{t("rules.ai_matches", "LIVE PREVIEW — {n} matching emails", { n: preview.count })}{preview.ai_evaluated ? " ⚡" : ""}</div>
              {preview.items.map((it) => <div key={it.id} className="text-xs text-gray-600 truncate">✉ {it.subject} <span className="text-gray-400">· {it.from_email}</span></div>)}
              {preview.count === 0 && <div className="text-xs text-gray-400">{t("rules.no_matches", "No past emails match — the rule will still watch future emails.")}</div>}
            </div>
          )}
          <div className="flex gap-2">
            <button data-testid="rule-preview-btn" onClick={() => runPreview()} disabled={previewing || (!form.trigger_value.trim() && !form.ai_condition.trim())}
              className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md px-4 py-1.5 transition-colors disabled:opacity-50">
              {previewing ? t("rules.previewing", "Checking…") : t("rules.preview", "Preview matches")}
            </button>
            <button data-testid="rule-create-btn" onClick={createManual} disabled={!form.trigger_value.trim() && !form.ai_condition.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-1.5 transition-colors disabled:opacity-50">
              {t("rules.create", "Create rule")}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {rules.map((r) => (
          <div key={r.id} data-testid={`rule-card-${r.id}`} className="border border-gray-200 rounded-md p-4 flex items-start gap-3">
            <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${r.active ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-400"}`}>
              <Lightning size={16} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                {r.name}
                {r.uses_ai && <span data-testid={`rule-ai-badge-${r.id}`} className="text-[10px] font-bold bg-purple-100 text-purple-700 rounded px-1.5 py-px">⚡ AI</span>}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {t("rules.when", "When")} <span className="font-medium text-gray-700">{t(...(TRIGGER_LABEL[r.trigger_type] || [r.trigger_type, r.trigger_type]))} "{r.trigger_value}"</span>
                {r.ai_condition && <span className="text-purple-600"> + "{r.ai_condition}"</span>}
                {" → "}<span className="font-medium text-gray-700">{ruleActionText(r)}</span>
              </div>
              {r.description && !r.ai_condition && <div className="text-xs text-gray-400 mt-1 italic">"{r.description}"</div>}
              <div className="text-[11px] text-gray-400 mt-1" data-testid={`rule-hits-${r.id}`}>
                {(r.hits || 0) > 0
                  ? `${t("rules.hits", "{n} hits", { n: r.hits })}${r.last_hit_at ? ` · ${t("rules.last_hit", "last")} ${relTime(r.last_hit_at)}` : ""}`
                  : t("rules.no_hits", "No hits yet")}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`rule-logs-${r.id}`} onClick={() => openLogs(r)} title={t("rules.view_logs", "View logs")}
                className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-blue-700 border border-gray-200 hover:border-blue-300 rounded-full px-3 py-1 transition-colors">
                <ListMagnifyingGlass size={13} /> {t("rules.view_logs", "View logs")}
              </button>
              <button data-testid={`rule-toggle-${r.id}`} onClick={() => toggle(r)}
                className={`text-xs font-semibold rounded-full px-3 py-1 transition-colors ${r.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                {r.active ? t("rules.active", "Active") : t("rules.paused", "Paused")}
              </button>
              <button data-testid={`rule-delete-${r.id}`} onClick={() => remove(r)} className="text-gray-400 hover:text-red-600 transition-colors p-1"><Trash size={15} /></button>
            </div>
          </div>
        ))}
        {rules.length === 0 && <p data-testid="no-rules-message" className="text-sm text-gray-400">{t("rules.none2", "No rules yet — create one above or ask your agent.")}</p>}
      </div>
      </>)}

      {logs && (
        <div data-testid="rule-logs-panel"
          className="fixed right-0 top-0 h-full w-[440px] max-w-[92vw] bg-white border-l border-gray-200 shadow-2xl z-40 flex flex-col animate-in slide-in-from-right duration-200">
          <div className="flex items-start justify-between px-4 py-3 border-b border-gray-200">
            <div className="min-w-0">
              <div className="text-sm font-bold text-gray-900 truncate">⚡ {logs.rule.name}</div>
              <div className="text-xs text-gray-500 mt-0.5" data-testid="rule-logs-count">
                {t("rules.log_sub", "{n} recorded hits — emails this rule acted on", { n: logs.items.length })}
              </div>
            </div>
            <button data-testid="rule-logs-close" onClick={() => setLogs(null)}
              className="text-gray-400 hover:text-gray-800 p-1 shrink-0 ml-2"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto scroll-thin">
            {logsLoading && <div className="p-4 text-sm text-gray-400">{t("common.loading", "Loading…")}</div>}
            {!logsLoading && logs.items.length === 0 && (
              <div data-testid="rule-logs-empty" className="p-6 text-sm text-gray-400 text-center">
                {t("rules.log_none", "This rule hasn't acted on any email yet. Hits will appear here as emails arrive or when you apply the rule to past emails.")}
              </div>
            )}
            {logs.items.map((it) => (
              <button key={it.id} data-testid={`rule-log-row-${it.id}`}
                onClick={() => it.email?.thread_id && navigate(`/app?thread=${it.email.thread_id}`)}
                className="w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-blue-50/40 transition-colors">
                <div className="flex justify-between gap-2">
                  <span className="text-[13px] font-semibold text-gray-900 truncate">{it.email?.from_name || it.email?.from_email || t("rules.log_gone", "(email deleted)")}</span>
                  <span className="text-[11px] text-gray-400 shrink-0">{relTime(it.created_at)}</span>
                </div>
                <div className="text-[13px] text-gray-700 truncate">{it.email?.subject || "—"}</div>
                <div className="text-xs text-gray-400 truncate">{it.email?.snippet || ""}</div>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-[10px] font-bold bg-blue-50 text-blue-700 rounded px-1.5 py-px">{ACTION_PAST[it.action_taken] || it.action_taken}</span>
                  {it.matched_by === "ai" && <span className="text-[10px] font-bold bg-purple-100 text-purple-700 rounded px-1.5 py-px">⚡ AI</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </PageShell>
  );
}
